import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QuoteBreakdown } from "@/components/features/notarial-requests/quote-breakdown";
import { formatUyMinor } from "@/lib/formatUyMinor";
import type { QuoteLineItemView } from "@/services/quoteLineItems";

export interface QuoteBreakdownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceMinor: number;
  currency: string;
  lineItems: QuoteLineItemView[] | null;
  pricesIncludeIva: boolean;
  /**
   * The platform's net fee and its IVA, computed by the caller from the
   * quote-form config.
   *
   * NULL WHEN THE CONFIG COULD NOT BE READ, and then the consumer-facing rows
   * are OMITTED rather than shown at zero. A fee printed as 0% is a lie about
   * someone's pay — it tells a professional their client is charged exactly
   * what they receive. Mobile's sheet established this rule (spec 031fbf43,
   * C-1); the portal loads the config higher up, so the null case is rarer
   * here, not impossible.
   */
  fee: { netMinor: number; ivaMinor: number } | null;
}

/**
 * The professional's own submitted quote, priced out in full.
 *
 * WHY IT IS A DIALOG. Mobile put this in a bottom sheet because a phone had no
 * room; the portal has room, and inline behind a disclosure was the cheaper
 * answer. The user chose the dialog so a professional who works both surfaces
 * meets the same thing in the same shape — and the card's job stays "here is
 * where your bid stands" rather than growing an itemisation plus four money
 * rows taller than everything around it.
 *
 * THE FEE APPEARS HERE AND NOWHERE ELSE on this page, which is the point of
 * having the dialog at all. `priceMinor` is what the professional RECEIVES;
 * the consumer is charged that plus Gestionalo's fee. The bid form shows both
 * figures while the quote is being written and then the professional never
 * sees them together again — this is where they come back.
 */
export function QuoteBreakdownDialog({
  open,
  onOpenChange,
  priceMinor,
  currency,
  lineItems,
  pricesIncludeIva,
  fee,
}: QuoteBreakdownDialogProps): JSX.Element {
  const { t } = useTranslation();

  const feeTotalMinor = fee == null ? null : fee.netMinor + fee.ivaMinor;
  const totalToChargeMinor = feeTotalMinor == null ? null : priceMinor + feeTotalMinor;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("notary.requestDetail.myQuoteBreakdownTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {lineItems != null && lineItems.length > 0 ? (
            <QuoteBreakdown
              lineItems={lineItems}
              currency={currency}
              pricesIncludeIva={pricesIncludeIva}
            />
          ) : null}

          {/* WHAT THE PROFESSIONAL RECEIVES comes first and is the emphasised
              figure, because it is theirs. The client's total sits under it,
              as context for a number they already know. */}
          <div className="flex items-baseline justify-between gap-4 border-t border-border pt-3">
            <dt className="text-sm font-medium text-foreground">
              {t("notary.requestDetail.myQuoteReceiveLabel")}
            </dt>
            <dd className="text-base font-semibold tabular-nums text-foreground">
              {formatUyMinor(priceMinor, currency)}
            </dd>
          </div>

          {fee != null && totalToChargeMinor != null ? (
            <dl className="space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">
                  {t("notary.requestDetail.myQuotePlatformFeeLabel")}
                </dt>
                <dd className="tabular-nums text-foreground">
                  {formatUyMinor(fee.netMinor, currency)}
                </dd>
              </div>
              {fee.ivaMinor > 0 ? (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-muted-foreground">
                    {t("notary.requestDetail.myQuotePlatformFeeIvaLabel")}
                  </dt>
                  <dd className="tabular-nums text-foreground">
                    {formatUyMinor(fee.ivaMinor, currency)}
                  </dd>
                </div>
              ) : null}
              <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5">
                <dt className="font-medium text-foreground">
                  {t("notary.requestDetail.myQuoteTotalToChargeLabel")}
                </dt>
                <dd className="font-semibold tabular-nums text-foreground">
                  {formatUyMinor(totalToChargeMinor, currency)}
                </dd>
              </div>
              <p className="pt-2 text-xs text-muted-foreground">
                {t("notary.requestDetail.myQuoteBreakdownHint")}
              </p>
            </dl>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
